import { Row, Tooltip } from 'antd';
import { useEffect, useState } from 'react';

import { EditOutlined } from '@ant-design/icons';
import formatUOM from '@common/helpers/formatUOM';
import { SelectedItemDetailEntry } from '@common/models';
import { COMING_SOON_IMAGE } from '@common/utils';
import { formatPriceUnitOrFree } from '@common/utils/formatPriceUnitOrFree';
import ProductCard from '@core/components/ProductCard/ProductCard';
import ConfigurableModal from '@core/components/modal/modal';
import ImageUtils from '@core/utils/ImageUtils';

import { SelectedProductDetailsTable } from './SelectedProductDetailsTable';
import { addDataRecords, dataRecordToDetailEntry, detailEntryToDataRecord } from './helpers/detail-entry-helpers';
import { DetailDataRecord } from './models/DetailDataRecord';

import './OptionalProductWithDetailsItem.scss';

interface OptionalProductWithDetailsItemProps {
    description: string;
    price: number;
    isSelected?: boolean;
    imgPath?: string;
    quantity?: number;
    onClick?: () => void;
    unitOfMeasure: string;
    detailEntries: SelectedItemDetailEntry[];
    onDetailSave: (entries: SelectedItemDetailEntry[]) => void;
    showModalOnOpen?: boolean;
}

const OptionalProductWithDetailsItem = ({
    description,
    price,
    isSelected = false,
    imgPath,
    quantity = 0,
    onClick,
    unitOfMeasure,
    detailEntries,
    onDetailSave,
    showModalOnOpen = false,
}: OptionalProductWithDetailsItemProps): JSX.Element => {
    const imgSrc = ImageUtils.imgUrl(imgPath || COMING_SOON_IMAGE, 300);
    const [showModal, setShowModal] = useState(false);
    const [dataSource, setDataSource] = useState<DetailDataRecord[]>([]);

    useEffect(() => {
        if (!showModal) {
            return;
        }
        setDataSource(
            addDataRecords(
                detailEntries.map((item, index) => detailEntryToDataRecord(item, index.toString())),
                Math.max(1, 3 - detailEntries.length)
            )
        );
    }, [showModal]);

    useEffect(() => {
        if (isSelected && showModalOnOpen && !showModal && quantity === 0) {
            setShowModal(true);
        }
    }, [isSelected]);

    const onCloseModal = (save: boolean) => {
        setShowModal(false);
        if (save) {
            const entries = dataSource
                .filter((item) => item.quantity > 0 || !!item.note)
                .map((entry) => dataRecordToDetailEntry(entry));
            onDetailSave(entries);
        } else {
            // When save = false meaning on Cancel. Save previous data to trigger deselection
            onDetailSave(detailEntries);
        }
    };

    return (
        <>
            <ConfigurableModal
                isVisible={showModal}
                onClose={onCloseModal}
                closable={false} // no X to close
                maskClosable={false} // don't cancel on outside click!
                keyboard={false} //no keyboard close
                title={description + ' Details'}
                width={700}
                content={
                    <SelectedProductDetailsTable
                        tableData={dataSource}
                        setTableData={setDataSource}
                        unitPrice={price}
                        onConfirm={() => onCloseModal(true)}
                        onCancel={() => onCloseModal(false)}
                    />
                }
                footer={null}
            />
            <ProductCard isSelected={isSelected} onClick={onClick} additionalClasses={{ 'multi-action-card': true }}>
                <ProductCard.Image src={imgSrc} />
                <ProductCard.Title>{description}</ProductCard.Title>
                <ProductCard.Subtitle>{formatPriceUnitOrFree(price, unitOfMeasure)}</ProductCard.Subtitle>
                <Row justify="end">
                    {quantity ? (
                        `${quantity} ${formatUOM(unitOfMeasure) ?? null}`
                    ) : (
                        <span
                            onClick={() => {
                                setShowModal(true);
                            }}
                        >
                            Edit Details
                        </span>
                    )}
                    <Tooltip title="Edit Details">
                        {/* TODO clean up this button, CSS class */}
                        <EditOutlined
                            className="OptionalProductWithDetailsEditButton"
                            onClick={() => {
                                setShowModal(true);
                            }}
                        />
                    </Tooltip>
                </Row>
            </ProductCard>
        </>
    );
};

export default OptionalProductWithDetailsItem;
